import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../ui/sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { AlertTriangle, CheckCircle2, Download, Loader2, Plus, RotateCcw, Save, Trash2, Ruler } from "lucide-react";
import { fmtDT } from "../../lib/date";

/**
 * WF-04-01-08 衣柜 rough opening 三联框测量面板。
 *
 * 【需求】2026-10-29 老板口述：
 *   「任务2 是上传多个衣柜的尺寸…衣柜1 衣柜2 衣柜3 的尺寸…输入应该是以英寸输入…
 *     然后交由中国同事去确认需要下单的尺寸」
 *   「美国人员在输入尺寸后需要显示系统计算尺寸为多少…然后是中国同事下单尺寸是多少…
 *     相当于是一个三联框的输入」
 *   分工：「任务2 是线下工程师上传尺寸…线上工程师下单填尺寸」
 *
 * 【三联框 · 一行三个框，谁能填按角色分】
 *   ① 现场 RO (in)  —— 只有现场 PM（线下工程师）能输入；线上 PM 看到的是只读文本
 *   ② 系统计算 (mm)  —— 只读，按后端 _calc_net_mm 的规则算（前端也做一份实时预估，见 calcNet）
 *   ③ 下单尺寸 (mm)  —— 只有线上 PM（中国同事）能输入；现场 PM 看到的是"待线上 PM 下单"
 *
 * 【为什么改成一张表】（2026-10-29 老板看实物后反馈：「没有看到线上 PM 输入下单尺寸的位置」）
 *   初版拆成「现场录入表 + 已确认表」，线上 PM 必须先等条目被确认过一次才有「修正」入口，
 *   未确认的条目在界面上根本没有可输入的下单尺寸框 —— 与需求的三联框完全不符。
 *   现在改成一行三个框：现场 PM 填最左的 ①，线上 PM 填最右的 ③，中间 ② 由系统算，谁也改不了别人的框。
 *
 * 【款式 / 扇数为什么在 ③ 而不是 ①】（老板二次口径：「不要这两个」，指现场录入表里的款式/扇数列）
 *   现场 PM 手上只有洞口宽高，款式由选材决定、扇数由下单方案决定，都不是他填得出来的东西。
 *   所以这两项跟着 ③ 下单尺寸一起由线上 PM 填，仍随下单清单导出给厂家（老板规则 4 / 5 不落空）。
 *
 * 【换算规则】（老板口述 5 条）
 *   1. Rough Opening × 25.4 转 mm
 *   2. 已装 drywall：只减地板
 *   3. 未装 drywall：减左右两侧 16mm + 顶部 16mm + 地板 6mm
 *   4. 数量与款式：数量 = 衣柜条目数，款式/扇数由线上 PM 在下单时登记
 *   5. 尺寸+数量+款式一起给厂家：由「导出下单清单」完成
 *
 * 【为什么前端也算一遍】② 列要在用户敲数字的当下就显示结果，等一次网络往返体验太差。
 * 后端仍是唯一权威（提交后以接口返回的 calcWidthMm 为准），前端这份只用于即时预览。
 * 两边的常量与公式必须一致 —— 若日后改规则，engine_closet_openings.py 与这里要同步改。
 */
const DRYWALL_MM = 16;
const FLOOR_MM = 6;
const INCH_MM = 25.4;

/** 前端即时预览用；与后端 _calc_net_mm 保持同一套规则。 */
function calcNet(wIn, hIn, drywallInstalled) {
  const w = parseFloat(wIn);
  const h = parseFloat(hIn);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return { w: null, h: null };
  const wMm = w * INCH_MM;
  const hMm = h * INCH_MM;
  const netW = drywallInstalled ? wMm : wMm - DRYWALL_MM * 2;
  const netH = drywallInstalled ? hMm - FLOOR_MM : hMm - DRYWALL_MM - FLOOR_MM;
  return { w: Math.round(Math.max(netW, 0) * 10) / 10, h: Math.round(Math.max(netH, 0) * 10) / 10 };
}

const fmtMm = (v) => (v === null || v === undefined || v === "" ? "—" : `${v}`);

export const ClosetOpeningsPanel = ({ api, pid, zh, onChanged }) => {
  const T = (a, b) => (zh ? a : b);
  const [d, setD] = useState(null);
  // 现场录入草稿：既包含已提交未确认的条目（saved=true，seq 来自服务端），也包含刚点「添加衣柜」的空行。
  const [rows, setRows] = useState([]);
  // ③ 下单尺寸草稿：seq → {orderWidthMm, orderHeightMm, style, panels}，由线上 PM 编辑。
  const [order, setOrder] = useState({});
  const [drywall, setDrywall] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  // 三联框配置弹窗开关：外层只放进度/按钮，表格在弹窗里（2026-10-29 老板：显示区域太小）
  const [cfgOpen, setCfgOpen] = useState(false);
  const keySeq = useRef(0);

  /**
   * 把接口返回的最新快照写进界面，**同时**重建两份草稿（现场 RO / 线上下单）。
   *
   * 【为什么必须一起做】初版 confirmAll / submitConfirm 只 setD 不动草稿，实测后果是：
   * 确认完成后同一批衣柜在「待录入」和「已确认」两张表里各出现一次，现场同事会误以为要重复录。
   * 任何改变条目状态的接口返回都必须走这里。
   */
  const applyState = useCallback((state) => {
    if (!state) return;
    setD(state);
    setDrywall(!!state.drywallInstalled);
    setRows((state.items || []).filter((i) => i.status !== "confirmed").map((i) => ({
      key: `s${i.seq}`, seq: i.seq, saved: true,
      roWidthIn: i.roWidthIn ?? "", roHeightIn: i.roHeightIn ?? "", note: i.note || "",
    })));
    const m = {};
    (state.items || []).forEach((i) => {
      // 未确认条目预填系统换算值（线上 PM 一键采纳即可），已确认条目预填已保存的下单值（再次编辑=修正）。
      m[i.seq] = {
        orderWidthMm: i.orderWidthMm ?? i.calcWidthMm ?? "",
        orderHeightMm: i.orderHeightMm ?? i.calcHeightMm ?? "",
        style: i.style || "",
        panels: i.panels ?? "",
      };
    });
    setOrder(m);
  }, []);

  const load = useCallback(
    () => api.get(`/projects/${pid}/engine/closet-openings`).then(({ data }) => applyState(data)).catch(() => {}),
    [api, pid, applyState]
  );
  useEffect(() => { load(); }, [load]);

  const canMeasure = !!d?.canMeasure;
  const canConfirm = !!d?.canConfirm;
  const draftBySeq = useMemo(() => {
    const m = {};
    rows.forEach((r) => { if (r.saved) m[r.seq] = r; });
    return m;
  }, [rows]);
  const newDrafts = useMemo(() => rows.filter((r) => !r.saved), [rows]);

  const addRow = () => { keySeq.current += 1; setRows((r) => [...r, { key: `n${keySeq.current}`, seq: 0, saved: false, roWidthIn: "", roHeightIn: "", note: "" }]); };
  const delRow = (key) => setRows((r) => r.filter((x) => x.key !== key));
  const patchRow = (key, k, v) => setRows((r) => r.map((x) => (x.key === key ? { ...x, [k]: v } : x)));
  const patchOrder = (seq, k, v) => setOrder((o) => ({ ...o, [seq]: { ...(o[seq] || {}), [k]: v } }));

  const saveDrywall = async (val) => {
    setDrywall(val); setBusy("drywall"); setErr("");
    try {
      await api.patch(`/projects/${pid}/engine/closet-openings/settings`, { drywallInstalled: val });
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || T("切换口径失败", "Failed to switch basis"));
      setDrywall(!val);
    } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save"); setErr("");
    try {
      // 整份覆盖：后端按 seq 保留已确认条目、只重建未确认的，所以这里把「已存的未确认行 + 新草稿」一起发，
      // 并且**必须带 seq** —— 不带的话后端只能按下标重新编号，一旦有条目已确认就会整体错位覆盖。
      const { data } = await api.put(`/projects/${pid}/engine/closet-openings/items`, {
        drywallInstalled: drywall,
        items: rows.map((r) => ({ seq: r.saved ? r.seq : undefined, roWidthIn: r.roWidthIn, roHeightIn: r.roHeightIn, note: r.note })),
      });
      applyState(data.state);
      toast.success(T("尺寸已提交，等待线上 PM 下单", "Submitted — awaiting online PM order sizes"));
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || T("保存失败", "Save failed");
      setErr(msg); toast.error(msg);
    } finally { setBusy(""); }
  };

  /** 线上 PM 逐条下单：把 ③ 框里的尺寸 + 款式/扇数一起提交。 */
  const confirmRow = async (seq) => {
    const o = order[seq] || {};
    setBusy(`confirm-${seq}`); setErr("");
    try {
      const { data } = await api.post(`/projects/${pid}/engine/closet-openings/confirm`, {
        seq,
        orderWidthMm: o.orderWidthMm,
        orderHeightMm: o.orderHeightMm,
        style: o.style,
        panels: o.panels,
      });
      applyState(data.state);
      toast.success(T(`衣柜 #${seq} 下单尺寸已确认`, `Closet #${seq} order size confirmed`));
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || T("确认失败", "Confirm failed");
      setErr(msg); toast.error(msg);
    } finally { setBusy(""); }
  };

  const confirmAll = async () => {
    setBusy("confirmAll"); setErr("");
    try {
      // 批量采纳只按系统换算值确认尺寸，不碰款式/扇数（它们的写入方是逐条下单）。
      const { data } = await api.post(`/projects/${pid}/engine/closet-openings/confirm`, {});
      applyState(data.state);
      toast.success(T(`已确认 ${data.confirmed} 个衣柜的下单尺寸`, `${data.confirmed} closet(s) confirmed`));
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || T("确认失败", "Confirm failed");
      setErr(msg); toast.error(msg);
    } finally { setBusy(""); }
  };

  const reopen = async (seq) => {
    if (!window.confirm(T(`退回衣柜 #${seq} 让现场重测？`, `Send closet #${seq} back for re-measurement?`))) return;
    setBusy(`reopen-${seq}`); setErr("");
    try {
      const { data } = await api.post(`/projects/${pid}/engine/closet-openings/reopen`, { seq });
      // 退回后该条目回到「待录入」，必须重建草稿行，否则现场看不到它需要重测。
      applyState(data.state);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || T("退回失败", "Reopen failed")); }
    finally { setBusy(""); }
  };

  const exportSheet = async () => {
    setBusy("export"); setErr("");
    try {
      const { data } = await api.get(`/projects/${pid}/engine/closet-openings/order-sheet`);
      const lines = [
        `ALIGCUS · ${T("衣柜门下单清单", "Closet Door Order Sheet")}`,
        `${T("项目", "Project")}: ${data.project?.address || ""}`,
        `${T("口径", "Basis")}: ${data.drywallInstalled ? T("已装 drywall（只减地板）", "Drywall installed (floor only)") : T("未装 drywall（减两侧+顶+地板）", "No drywall (sides + top + floor)")}`,
        `${T("合计", "Total")}: ${data.totalCount} ${T("樘", "doors")} / ${data.totalPanels} ${T("扇", "panels")}`,
        "",
        `${T("衣柜", "Closet")}\t${T("宽 mm", "Width mm")}\t${T("高 mm", "Height mm")}\t${T("款式", "Style")}\t${T("扇数", "Panels")}\t${T("现场 RO (in)", "Site RO (in)")}`,
        ...data.rows.map((r) => `#${r.seq}\t${r.widthMm}\t${r.heightMm}\t${r.style || "-"}\t${r.panels}\t${r.roIn}${r.adjusted ? " *" : ""}`),
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `closet_order_${pid}.txt`; a.click();
      URL.revokeObjectURL(url);
      toast.success(T("下单清单已导出", "Order sheet exported"));
    } catch (e) {
      const msg = e?.response?.data?.detail || T("暂无可导出的已确认尺寸", "No confirmed sizes to export");
      setErr(msg); toast.error(msg);
    } finally { setBusy(""); }
  };

  if (!d) return null;

  const th = "text-[9px] font-bold uppercase tracking-wider text-slate-400 px-1";
  const cell = "border border-slate-200 px-1.5 py-1 align-middle";
  const inp = "rounded border border-slate-200 px-1.5 py-1 text-[11px] disabled:bg-transparent disabled:border-transparent disabled:text-slate-500";
  const rowBtn = "text-[9.5px] font-bold rounded border px-1.5 py-0.5 disabled:opacity-40";

  /** 三个框的底色：① 现场（靛蓝）② 系统（灰）③ 线上（翠绿），让人一眼看出谁填哪一格。 */
  const boxA = "bg-indigo-50/40";
  const boxB = "bg-slate-50";
  const boxC = "bg-emerald-50/30";

  /** 渲染一行：已存条目（submitted/confirmed）或新建草稿。 */
  const renderRow = ({ seq, saved, confirmed, draft, item, idx }) => {
    // testid 后缀：已存条目一律用后端分配的 seq，未保存的草稿用 d{下标}。
    // 混用同一个数值空间会撞 id —— 已确认条目占着 seq=1 时，草稿下标 0/1 若也用纯数字，
    // 就会出现两个 trace-ws-closet-w-1，自动化取输入框会取错行。
    const tag = saved ? seq : `d${idx}`;
    const stale = confirmed && (d.staleSeqs || []).includes(seq);
    const net = confirmed
      ? { w: item.calcWidthMm, h: item.calcHeightMm }
      : calcNet(draft?.roWidthIn, draft?.roHeightIn, drywall);
    const o = order[seq] || {};
    return (
      <tr key={saved ? `s${seq}` : draft.key}
        data-testid={confirmed ? `trace-ws-closet-crow-${seq}` : `trace-ws-closet-row-${idx}`}
        data-seq={seq}
        className={stale ? "bg-amber-50" : ""}>
        <td className={`${cell} text-center font-bold text-slate-600`}>
          #{seq}
          {!saved && <div className="text-[8px] font-normal text-slate-400">{T("未提交", "new")}</div>}
        </td>

        {/* ① 现场 PM 录入：只有线下工程师能输入；线上 PM 看到的是只读文本（老板：每一格只归一方填） */}
        <td className={`${cell} ${boxA} border-l-2 border-l-indigo-200`}>
          {canMeasure && !confirmed ? (
            <input type="number" inputMode="decimal" min="0" step="0.25"
              value={draft?.roWidthIn ?? ""}
              disabled={busy === "save"}
              onChange={(e) => draft && patchRow(draft.key, "roWidthIn", e.target.value)}
              placeholder="60" data-testid={`trace-ws-closet-w-${tag}`}
              className={`w-full min-w-0 ${inp}`} />
          ) : (
            <span className="font-mono text-[11px] text-slate-700" data-testid={`trace-ws-closet-ro-${tag}`}>
              {confirmed ? item.roWidthIn : (draft?.roWidthIn || "—")}
            </span>
          )}
        </td>
        <td className={`${cell} ${boxA}`}>
          {canMeasure && !confirmed ? (
            <input type="number" inputMode="decimal" min="0" step="0.25"
              value={draft?.roHeightIn ?? ""}
              disabled={busy === "save"}
              onChange={(e) => draft && patchRow(draft.key, "roHeightIn", e.target.value)}
              placeholder="80" data-testid={`trace-ws-closet-h-${tag}`}
              className={`w-full min-w-0 ${inp}`} />
          ) : (
            <span className="font-mono text-[11px] text-slate-700">
              {confirmed ? item.roHeightIn : (draft?.roHeightIn || "—")}
            </span>
          )}
        </td>

        {/* ② 系统计算：只读，谁也改不了 */}
        <td className={`${cell} ${boxB} border-l-2 border-l-slate-200`} data-testid={`trace-ws-closet-calc-${tag}`}>
          <span className="font-mono text-[11px] text-slate-600">{fmtMm(net.w)} × {fmtMm(net.h)}</span>
        </td>

        {/* ③ 线上 PM 下单：只有中国同事能输入 */}
        <td className={`${cell} ${boxC} border-l-2 border-l-emerald-200`}>
          {canConfirm && saved ? (
            <input type="number" inputMode="decimal" min="0" step="0.1" value={o.orderWidthMm ?? ""}
              onChange={(e) => patchOrder(seq, "orderWidthMm", e.target.value)}
              placeholder={fmtMm(net.w)} data-testid={`trace-ws-closet-orderw-${seq}`}
              className={`w-full min-w-0 ${inp}`} />
          ) : (
            <span className="font-mono text-[11px] text-emerald-700" data-testid={`trace-ws-closet-order-${seq}`}>
              {confirmed ? fmtMm(item.orderWidthMm) : T("待线上 PM", "pending")}
              {confirmed && item.confirmedAdjusted && <span className="ml-0.5 text-[9px] text-amber-600" title={T("已按实际修正", "adjusted")}>*</span>}
            </span>
          )}
        </td>
        <td className={`${cell} ${boxC}`}>
          {canConfirm && saved ? (
            <input type="number" inputMode="decimal" min="0" step="0.1" value={o.orderHeightMm ?? ""}
              onChange={(e) => patchOrder(seq, "orderHeightMm", e.target.value)}
              placeholder={fmtMm(net.h)} data-testid={`trace-ws-closet-orderh-${seq}`}
              className={`w-full min-w-0 ${inp}`} />
          ) : (
            <span className="font-mono text-[11px] text-emerald-700">{confirmed ? fmtMm(item.orderHeightMm) : "—"}</span>
          )}
        </td>

        {/* 款式 / 扇数：跟着 ③ 由线上 PM 填（现场表已按老板口径去掉这两列） */}
        <td className={`${cell} ${boxC}`}>
          {canConfirm && saved ? (
            <div className="flex items-center gap-1">
              <input value={o.style ?? ""} onChange={(e) => patchOrder(seq, "style", e.target.value)}
                placeholder={T("款式", "Style")} data-testid={`trace-ws-closet-style-${seq}`}
                className={`w-full min-w-0 ${inp}`} />
              <input type="number" min="0" max="8" value={o.panels ?? ""}
                onChange={(e) => patchOrder(seq, "panels", e.target.value)}
                placeholder={T("扇数", "Panels")} data-testid={`trace-ws-closet-panels-${seq}`}
                className={`w-12 shrink-0 ${inp}`} />
            </div>
          ) : (
            <span className="text-[10px] text-slate-600 break-words" data-testid={`trace-ws-closet-meta-${seq}`}>
              {confirmed
                ? (item.style || item.panels
                  ? `${item.style || "—"} / ${item.panels || 0}`
                  : <span className="text-amber-700">{T("未填款式/扇数", "not set")}</span>)
                : "—"}
            </span>
          )}
        </td>

        <td className={`${cell} text-center whitespace-nowrap`}>
          {/* 线上 PM：未确认/需重确认 → 下单；已确认 → 修正 + 退回 */}
          {canConfirm && saved && (!confirmed || stale) && (
            <button type="button" disabled={busy === `confirm-${seq}`} onClick={() => confirmRow(seq)}
              data-testid={`trace-ws-closet-edit-${seq}`}
              className={`${rowBtn} border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1`}>
              {busy === `confirm-${seq}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              {T("下单", "Order")}
            </button>
          )}
          {canConfirm && confirmed && !stale && (
            <div className="flex items-center justify-center gap-1">
              <button type="button" disabled={busy === `confirm-${seq}`} onClick={() => confirmRow(seq)}
                data-testid={`trace-ws-closet-edit-${seq}`} title={T("修正下单尺寸/款式", "Adjust order")}
                className={`${rowBtn} border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
                {T("修正", "Edit")}
              </button>
              <button type="button" disabled={busy === `reopen-${seq}`} onClick={() => reopen(seq)}
                data-testid={`trace-ws-closet-reopen-${seq}`} title={T("退回重测", "Send back")}
                className="text-slate-300 hover:text-amber-600"><RotateCcw className="h-3 w-3" /></button>
            </div>
          )}
          {/* 现场 PM：未确认 = 可删（随整份提交一起生效） */}
          {canMeasure && !confirmed && draft && (
            <button type="button" disabled={busy === "save"} onClick={() => delRow(draft.key)}
              data-testid={`trace-ws-closet-delrow-${tag}`} title={T("删除该衣柜", "Remove closet")}
              className="text-slate-300 hover:text-red-600 disabled:opacity-40"><Trash2 className="h-3 w-3" /></button>
          )}
          {confirmed && (
            <div className="text-[8px] leading-tight text-slate-400 mt-0.5 truncate"
              title={`${item.confirmedBy || ""}${item.confirmedAt ? ` · ${fmtDT(item.confirmedAt)}` : ""}`}>
              <div className="truncate">{item.confirmedBy || ""}</div>
              {item.confirmedAt && <div className="truncate">{fmtDT(item.confirmedAt)}</div>}
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2" data-testid="trace-ws-closet">
      {/* 外层只负责「现在进展到哪一步」+ 入口按钮。
          【2026-10-29 改】原先把整张三联框直接铺在任务卡里，实测在 1440 视口下横向塞 8 列，
          日期/确认人挤成两行、表格右侧还被裁掉，录入体验很差。现在表格收进弹窗，
          外层保留进度与操作，需要配置时点开弹窗在宽屏里编辑。 */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
          <Ruler className="h-3 w-3" />{T("衣柜 rough opening · 三联框（① 现场英寸 → ② 系统毫米 → ③ 线上 PM 下单）", "Closet rough opening · ① site inch → ② auto mm → ③ online PM order")}
        </p>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
          data-testid="trace-ws-closet-summary" data-count={d.count} data-confirmed={d.confirmedCount}>
          {T(`${d.confirmedCount}/${d.count} 已确认`, `${d.confirmedCount}/${d.count} confirmed`)}
        </span>
      </div>

      {/* 进度条：一眼看出还有几个衣柜没下单 */}
      <div className="space-y-1" data-testid="trace-ws-closet-progress">
        <Progress value={d.count ? Math.round((d.confirmedCount / d.count) * 100) : 0}
          className="h-1.5 bg-slate-100" />
        <p className="text-[10px] text-slate-500">
          {d.count === 0
            ? T("尚未录入衣柜尺寸", "No closets recorded yet")
            : T(`共 ${d.count} 个衣柜 · 已下单 ${d.confirmedCount} 个 · 待下单 ${d.count - d.confirmedCount} 个`,
                `${d.count} closets · ${d.confirmedCount} ordered · ${d.count - d.confirmedCount} pending`)}
          <span className="mx-1.5 text-slate-300">|</span>
          {drywall
            ? T("口径：已装 drywall（只减地板 6mm）", "Basis: drywall installed (floor −6mm only)")
            : T("口径：未装 drywall（宽 −2×16mm，高 −16mm+6mm）", "Basis: no drywall (W −2×16mm, H −16mm+6mm)")}
        </p>
      </div>

      {/* 口径漂移提示：外层也要看得见，否则关掉弹窗就以为没事了（弹窗里还有一条可操作的） */}
      {d.hasStale && (
        <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1"
          data-testid="trace-ws-closet-stale-hint">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          {T(`drywall 口径已变更，${(d.staleSeqs || []).length} 个衣柜的下单尺寸需重新确认`,
            `Basis changed — ${(d.staleSeqs || []).length} closet(s) need re-confirmation`)}
        </p>
      )}

      {err && <p className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="trace-ws-closet-err">{err}</p>}

      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => setCfgOpen(true)}
          data-testid="trace-ws-closet-open"
          className="text-[10.5px] font-bold rounded-md bg-slate-900 text-white hover:bg-slate-700 px-2.5 py-1 inline-flex items-center gap-1">
          <Ruler className="h-3 w-3" />
          {canMeasure && !canConfirm
            ? T("录入 / 查看衣柜尺寸", "Enter / view closets")
            : T("配置三联框（录入 + 下单）", "Configure three columns")}
        </button>
        <button type="button" disabled={busy === "export" || d.confirmedCount === 0} onClick={exportSheet}
          data-testid="trace-ws-closet-export"
          className="text-[10.5px] font-bold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40">
          {busy === "export" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          {T("导出下单清单", "Export order sheet")}
        </button>
        {d.allConfirmed ? (
          <span className="text-[10px] text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid="trace-ws-closet-ready">
            <CheckCircle2 className="h-3 w-3" />{T("全部已确认，可以完成任务", "All confirmed — task can be completed")}
          </span>
        ) : d.count > 0 && (
          <span className="text-[10px] text-slate-400" data-testid="trace-ws-closet-hint">
            {T("现场 PM 填 ① 并提交 → 线上 PM 填 ③ 下单尺寸并逐条下单", "Site PM fills ①, online PM fills ③ and orders each")}
          </span>
        )}
      </div>

      {/* 弹窗：三联框本体。宽屏（max-w-5xl）才有足够横向空间放下 3 组列。 */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-w-5xl" data-testid="trace-ws-closet-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5 text-base">
              <Ruler className="h-4 w-4 text-brand-600" />
              {T("衣柜 rough opening 三联框", "Closet rough opening · three columns")}
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              {T("① 现场 PM 只填洞口英寸；② 系统按 drywall / 地板厚度自动换算毫米；③ 线上 PM 确认下单毫米并登记款式与扇数。每一格只归一方填写。",
                "① Site PM enters the rough opening in inches; ② the system converts to mm; ③ the online PM confirms order mm plus style and panels. Each column belongs to one role.")}
            </DialogDescription>
          </DialogHeader>

          {/* 口径切换：框架阶段默认未装 drywall（老板要求"框架 系统中默认要减"） */}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 rounded-md bg-slate-50 border border-slate-200 px-2.5 py-2"
            data-testid="trace-ws-closet-drywall-row">
            <input type="checkbox" checked={drywall} disabled={!canMeasure || busy === "drywall"}
              onChange={(e) => saveDrywall(e.target.checked)}
              data-testid="trace-ws-closet-drywall" className="h-3.5 w-3.5" />
            {T("drywall 已安装（只减地板 6mm）", "Drywall already installed (subtract floor 6mm only)")}
            <span className="text-[10px] text-slate-400">
              {drywall
                ? T("宽不减、高减 6mm", "width as-is, height −6mm")
                : T("宽减 2×16mm、高减 16mm+6mm", "width −2×16mm, height −16mm+6mm")}
            </span>
          </label>

          {d.hasStale && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 flex items-start gap-1"
              data-testid="trace-ws-closet-stale">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {T(`drywall 口径已变更，衣柜 ${(d.staleSeqs || []).map((s) => `#${s}`).join("、")} 的下单尺寸需重新确认`,
                `Basis changed — re-confirm order sizes for ${(d.staleSeqs || []).map((s) => `#${s}`).join(", ")}`)}
            </p>
          )}

          {/* 三联框表格：一行一个衣柜，三个框并排。
              【2026-10-29 修】初版把「现场录入」和「已确认」拆成两张表，未确认条目在界面上
              既没有 ③ 下单尺寸的输入框、也没有任何下单入口 —— 线上 PM 实测"找不到填的地方"。
              现在统一成一张表：① 只有现场 PM 能填、③ 只有线上 PM 能填，角色不同看到的是文本。 */}
          <div className="overflow-x-auto" data-testid="trace-ws-closet-entry">
            {d.count === 0 && newDrafts.length === 0 && (
              <p className="text-[11px] text-slate-400 px-1 py-1.5" data-testid="trace-ws-closet-empty">
                {T("还没有衣柜尺寸，点下面「添加衣柜」按现场数量逐个录入", "No closets yet — click “Add closet” and enter one row per opening")}
              </p>
            )}
            {(d.count > 0 || newDrafts.length > 0) && (
              <table className="w-full border-collapse text-[11px] table-fixed">
                {/* 列宽按「谁能填」分配：② 只读给最少空间，③ 要放下两个尺寸框 + 款式/扇数给最多，
                    操作列单独留 13%（含「下单/修正」按钮与确认人+时间两行），否则 1440 下右列会被裁。 */}
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "21%" }} />
                  <col style={{ width: "13%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={th} rowSpan={2}>{T("衣柜", "Closet")}</th>
                    <th className={`${th} text-center border-l-2 border-l-indigo-200 bg-indigo-50/60`} colSpan={2}>
                      {T("① 现场 PM 录入 · RO (in)", "① Site PM · RO (in)")}
                    </th>
                    <th className={`${th} text-center border-l-2 border-l-slate-200 bg-slate-100`}>
                      {T("② 系统计算 (mm)", "② Auto (mm)")}
                    </th>
                    <th className={`${th} text-center border-l-2 border-l-emerald-200 bg-emerald-50/60`} colSpan={3}>
                      {T("③ 线上 PM 下单 (mm) · 款式/扇数", "③ Online PM order (mm) · style/panels")}
                    </th>
                    <th className={th} rowSpan={2} />
                  </tr>
                  <tr>
                    <th className={`${th} text-center border-l-2 border-l-indigo-200 bg-indigo-50/30`}>{T("宽", "W")}</th>
                    <th className={`${th} text-center bg-indigo-50/30`}>{T("高", "H")}</th>
                    <th className={`${th} text-center border-l-2 border-l-slate-200 bg-slate-100`}>{T("宽 × 高", "W × H")}</th>
                    <th className={`${th} text-center border-l-2 border-l-emerald-200 bg-emerald-50/30`}>{T("宽", "W")}</th>
                    <th className={`${th} text-center bg-emerald-50/30`}>{T("高", "H")}</th>
                    <th className={`${th} text-center bg-emerald-50/30`}>{T("款式 / 扇数", "Style / panels")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.items || []).map((i, idx) => renderRow({
                    seq: i.seq,
                    saved: true,
                    confirmed: i.status === "confirmed",
                    draft: draftBySeq[i.seq],
                    item: i,
                    idx,
                  }))}
                  {newDrafts.map((r, i) => renderRow({
                    seq: (d.items || []).length + i + 1,
                    saved: false,
                    confirmed: false,
                    draft: r,
                    item: null,
                    idx: (d.items || []).length + i,
                  }))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap border-t border-slate-100 pt-2">
            <button type="button" disabled={!canMeasure || busy === "save"} onClick={addRow}
              data-testid="trace-ws-closet-add"
              className="text-[11px] font-bold rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-40">
              <Plus className="h-3.5 w-3.5" />{T("添加衣柜", "Add closet")}
            </button>
            <button type="button" disabled={!canMeasure || busy === "save" || rows.length === 0} onClick={save}
              data-testid="trace-ws-closet-save"
              className="text-[11px] font-bold rounded-md bg-slate-900 text-white hover:bg-slate-700 px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-40">
              {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {T("提交尺寸", "Submit sizes")}
            </button>
            {/* 批量采纳：口径漂移时按钮仍要出现（否则面板提示"需重新确认"却无路可走，实测卡死） */}
            {canConfirm && (d.submittedCount > d.confirmedCount || (d.staleSeqs || []).length > 0) && (
              <button type="button" disabled={busy === "confirmAll"} onClick={confirmAll}
                data-testid="trace-ws-closet-confirm-all"
                className="text-[11px] font-bold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-40">
                {busy === "confirmAll" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {(d.staleSeqs || []).length > 0
                  ? T(`按当前口径重新确认（${(d.staleSeqs || []).length} 个）`, `Re-confirm on current basis (${(d.staleSeqs || []).length})`)
                  : T(`全部采纳系统换算值（${d.submittedCount - d.confirmedCount} 个）`, `Accept all auto values (${d.submittedCount - d.confirmedCount})`)}
              </button>
            )}
            <span className="text-[10px] text-slate-400">{T("数量自由增减，输入几个就是几个", "Add or remove rows freely")}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
